import type { StaticEntityType } from "@config";

/**
 * Sentinel understood by the button styled-components: resolve this setting
 * against the design system's CSS variable instead of a stored value.
 */
export const DESIGN_SYSTEM_SETTING = "design-system";

/** Button settings that should follow the design system, not the preset. */
const BORDER_WIDTH_KEYS = ["borderWidth", "borderWidthSecondary"] as const;

/**
 * Element names that render through the button styles and therefore
 * understand the sentinel. `AcceptButton` (Seal the deal) is a separate
 * component with its own stylesheet, but that stylesheet resolves border
 * width through the same `resolveBorderWidthCss`, and its library presets
 * carry the same hardcoded `borderWidth: "1"` — so it needs the same
 * treatment or it keeps overriding the design system.
 */
const BUTTON_ELEMENT_NAMES = new Set(["Button", "AcceptButton"]);

const isButton = (settings: unknown): settings is Record<string, unknown> =>
  !!settings &&
  typeof settings === "object" &&
  BUTTON_ELEMENT_NAMES.has(
    (settings as Record<string, unknown>).name as string,
  );

/**
 * Point a freshly inserted button's border width at the design system.
 *
 * Every button preset in the elements library ships a hardcoded
 * `borderWidth: "1"` (and `borderWidthSecondary: "1"`). Because an explicit
 * per-element width always beats the design system, a button added from the
 * elements bar rendered a 1px border no matter what the design system's
 * Border → Width control said — the "buttons don't fully pick up the design
 * system settings" report (Monday #11838050027), and the elements-bar half of
 * "button borders displayed incorrectly" (Monday #12081734250).
 *
 * Colors and corner radius already defer correctly: the presets store
 * `var(--button-…)` references and `getDesignSystemValue` rewrites the ones
 * that land on a secondary slot. Width was the only setting with no route
 * from the design system to the rendered button, so it is the only one
 * rewritten here.
 *
 * The user can still set an explicit width afterwards — the settings slider
 * writes a number, which wins over the sentinel.
 */
export const deferButtonBorderToDesignSystem = <T extends StaticEntityType>(
  entity: T,
): T => {
  if (!entity) return entity;

  let next = entity;

  if (isButton(entity.settings)) {
    const settings = entity.settings as Record<string, unknown>;
    const patch: Record<string, unknown> = {};
    for (const key of BORDER_WIDTH_KEYS) {
      if (key in settings && settings[key] !== DESIGN_SYSTEM_SETTING) {
        patch[key] = DESIGN_SYSTEM_SETTING;
      }
    }
    if (Object.keys(patch).length > 0) {
      next = { ...next, settings: { ...settings, ...patch } } as T;
    }
  }

  if (next.children?.length) {
    next = {
      ...next,
      children: next.children.map(deferButtonBorderToDesignSystem),
    } as T;
  }

  return next;
};
